import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Heart, ChevronRight, RefreshCw, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import GuidedChatPage from "@/pages/GuidedChatPage";

const serifFont = { fontFamily: "'Playfair Display', serif" };

const ConversationsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [mutualMatches, setMutualMatches] = useState([]);
  const [pendingLikes, setPendingLikes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);

  const openChat = (userId) => {
    if (window.innerWidth >= 1024) {
      setSelectedChat(userId);
    } else {
      navigate(`/chat/${userId}`);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      // Get mutual matches
      const mutualRes = await api.getMutualMatches();
      console.log('[ConversationsPage] Mutual matches response:', {
        count: mutualRes.data.count,
        user_id: mutualRes.data.user_id,
        matches: mutualRes.data.mutual_matches?.length || mutualRes.data.matches?.length || 0
      });
      const mutual = mutualRes.data.mutual_matches || mutualRes.data.matches || [];
      setMutualMatches(mutual);

      // Get who liked me count
      try {
        const likesRes = await api.getWhoLikedMe();
        console.log('[ConversationsPage] Likes count:', likesRes.data?.count || 0);
        setPendingLikes(likesRes.data?.count || 0);
      } catch (err) {
        console.error("Failed to fetch likes count:", err.response?.data || err.message);
      }

      // Get conversations
      try {
        const convoRes = await api.getConversations();
        console.log('[ConversationsPage] Conversations response:', {
          count: convoRes.data?.count,
          user_id: convoRes.data?.user_id,
          conversations: convoRes.data?.conversations?.length || 0,
          error: convoRes.data?.error
        });
        setConversations(convoRes.data?.conversations || []);
      } catch (err) {
        console.error("Failed to fetch conversations:", err.response?.data || err.message);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error.response?.data || error.message);
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d`;
    return date.toLocaleDateString();
  };

  // Filter out mutual matches that already have conversations
  const conversationUserIds = new Set(conversations.map(c => c.other_user_id));
  const newMatches = mutualMatches.filter(m => !conversationUserIds.has(m.user_id));
  const totalConversations = conversations.length + newMatches.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FAB710]/30 border-t-[#FAB710] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa] lg:h-screen lg:flex lg:overflow-hidden" data-testid="conversations-page">
      {/* LEFT COLUMN (full page on mobile) */}
      <div className="pb-24 lg:pb-0 lg:w-[400px] lg:flex-shrink-0 lg:h-full lg:overflow-y-auto" style={{ borderRight: "1px solid rgba(10,42,90,0.08)" }}>
      {/* Desktop column header */}
      <div className="hidden lg:flex items-center justify-between px-6 pt-8 pb-2">
        <div>
          <h1 className="text-3xl" style={{ ...serifFont, color: "#0A2A5A" }}>Messages</h1>
          <p className="text-sm text-slate-500 mt-1">
            {totalConversations} conversation{totalConversations !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          className="p-2 rounded-full hover:bg-[#0A2A5A]/5 transition-colors"
          disabled={refreshing}
          data-testid="refresh-messages-desktop-btn"
        >
          <RefreshCw className={`w-5 h-5 text-[#0A2A5A] ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {/* Themed Gradient Header (mobile) */}
      <div 
        className="relative pt-12 pb-6 px-4 flex flex-col items-center overflow-hidden lg:hidden"
        style={{
          background: "linear-gradient(135deg, #1C3A7A 0%, #0d1f3c 50%, #061022 100%)",
          borderBottomLeftRadius: "25px",
          borderBottomRightRadius: "25px",
        }}
      >
        {/* Decorative Circles */}
        <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-white/[0.08]"></div>
        <div className="absolute bottom-3 -left-5 w-16 h-16 rounded-full bg-white/[0.05]"></div>

        {/* Refresh Button */}
        <button
          onClick={handleRefresh}
          className="absolute top-12 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          disabled={refreshing}
          data-testid="refresh-messages-btn"
        >
          <RefreshCw className={`w-5 h-5 text-white ${refreshing ? 'animate-spin' : ''}`} />
        </button>

        {/* Centered Icon */}
        <div 
          className="w-12 h-12 rounded-full flex items-center justify-center mb-2 shadow-lg"
          style={{
            background: "linear-gradient(135deg, #FAB710 0%, #FFD966 100%)",
            boxShadow: "0 4px 8px rgba(250, 183, 16, 0.3)"
          }}
        >
          <MessageCircle className="w-6 h-6 text-white" />
        </div>

        <h1 className="text-2xl font-bold text-white text-center drop-shadow-sm">
          Messages
        </h1>
        <p className="text-sm text-white/85 mt-1 text-center">
          {totalConversations} conversation{totalConversations !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Recent Matches Section */}
      {(mutualMatches.length > 0 || pendingLikes > 0) && (
        <div 
          className="bg-white pt-4 pb-5 -mt-2 shadow-sm"
          style={{
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
          }}
        >
          <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wide ml-4 mb-3">
            Recent Matches
          </h2>
          <div className="flex overflow-x-auto px-4 gap-3 scrollbar-hide">
            {/* Likes Card */}
            <button
              onClick={() => navigate("/matches")}
              className="flex-shrink-0 w-[85px] h-[110px] rounded-xl overflow-hidden shadow-md"
              style={{
                background: "linear-gradient(135deg, #FFE17D 0%, #FFC629 100%)",
                boxShadow: "0 3px 6px rgba(250, 183, 16, 0.2)"
              }}
              data-testid="likes-card"
            >
              <div className="w-full h-full flex flex-col items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-white/30 flex items-center justify-center mb-2">
                  <Heart className="w-7 h-7 text-white fill-white" />
                </div>
                <span className="text-xl font-bold text-white">{pendingLikes}</span>
              </div>
            </button>

            {/* Recent Match Cards */}
            {mutualMatches.slice(0, 10).map((match) => (
              <button
                key={match.user_id || match.matched_user_id}
                onClick={() => openChat(match.user_id || match.matched_user_id)}
                className="flex-shrink-0 w-[85px] h-[110px] rounded-xl overflow-hidden shadow-md relative bg-gray-200"
                data-testid={`recent-match-${match.user_id || match.matched_user_id}`}
              >
                {(match.photos?.[0] || match.photo) ? (
                  <img 
                    src={match.photos?.[0] || match.photo} 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-2xl font-bold">
                    {match.name?.charAt(0) || "?"}
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 py-1.5 px-2 bg-black/50">
                  <p className="text-xs font-semibold text-white text-center truncate">
                    {match.name?.split(" ")[0]}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Conversations Section */}
      <div className="pt-4 px-4 min-h-[400px]">
        {/* New Matches (no conversations yet) */}
        {newMatches.map((match) => (
          <button
            key={`new-${match.user_id || match.matched_user_id}`}
            onClick={() => openChat(match.user_id || match.matched_user_id)}
            className="w-full bg-white rounded-2xl p-3.5 flex items-center gap-3.5 shadow-sm mb-3 text-left hover:shadow-md transition-shadow"
            data-testid={`new-match-${match.user_id || match.matched_user_id}`}
          >
            {/* Avatar with New Match Dot */}
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 rounded-full bg-gray-200 overflow-hidden">
                {(match.photos?.[0] || match.photo) ? (
                  <img 
                    src={match.photos?.[0] || match.photo} 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-lg font-bold">
                    {match.name?.charAt(0) || "?"}
                  </div>
                )}
              </div>
              <div className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-green-500 border-2 border-white"></div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-bold text-gray-900 truncate">{match.name}</h3>
                <span className="px-2 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded-full">
                  NEW
                </span>
              </div>
              <p className="text-sm text-gray-500">
                You matched! Say hello 👋
              </p>
            </div>
          </button>
        ))}

        {/* Existing Conversations */}
        {conversations.map((convo) => {
          const hasUnread = convo.unread_count > 0;
          const isSelected = selectedChat === convo.other_user_id;
          return (
            <button
              key={convo.match_id || convo.other_user_id}
              onClick={() => openChat(convo.other_user_id)}
              className={`w-full bg-white rounded-2xl p-3.5 flex items-center gap-3.5 shadow-sm mb-3 text-left hover:shadow-md transition-shadow ${isSelected ? 'lg:ring-2 lg:ring-[#F9AB0D]' : ''}`}
              data-testid={`conversation-${convo.other_user_id}`}
            >
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div className="w-14 h-14 rounded-full bg-gray-200 overflow-hidden">
                  {convo.other_user_photo ? (
                    <img 
                      src={convo.other_user_photo} 
                      alt="" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-lg font-bold">
                      {convo.other_user_name?.charAt(0) || "?"}
                    </div>
                  )}
                </div>
                {hasUnread && (
                  <div className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-[#FAB710] border-2 border-white"></div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h3 className={`truncate ${hasUnread ? 'font-bold text-gray-900' : 'font-semibold text-gray-900'}`}>
                    {convo.other_user_name}
                  </h3>
                  <span className={`text-xs flex-shrink-0 ${hasUnread ? 'text-[#FAB710] font-semibold' : 'text-gray-400'}`}>
                    {formatTime(convo.last_message_at)}
                  </span>
                </div>
                <p className={`text-sm truncate ${hasUnread ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
                  {convo.last_message || "Start the conversation..."}
                </p>
              </div>
            </button>
          );
        })}

        {/* Empty State */}
        {conversations.length === 0 && newMatches.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 px-8">
            <div 
              className="w-24 h-24 rounded-full flex items-center justify-center mb-5"
              style={{
                background: "linear-gradient(135deg, rgba(28, 58, 122, 0.15) 0%, rgba(250, 183, 16, 0.1) 100%)"
              }}
            >
              <MessageCircle className="w-14 h-14 text-[#1C3A7A]" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">No Messages Yet</h2>
            <p className="text-gray-500 text-center text-sm leading-relaxed mb-6">
              When you match with someone, you can start chatting here
            </p>
            <button
              onClick={() => navigate("/matches")}
              className="px-8 py-3.5 rounded-full font-bold text-white shadow-lg"
              style={{
                background: "linear-gradient(135deg, #1C3A7A 0%, #0d1f3c 50%, #061022 100%)",
                boxShadow: "0 4px 8px rgba(28, 58, 122, 0.3)"
              }}
              data-testid="find-matches-btn"
            >
              Find Matches
            </button>
          </div>
        )}
      </div>

      <BottomNav />
      </div>

      {/* RIGHT PANEL — desktop embedded chat */}
      <div className="hidden lg:flex flex-1 h-full flex-col bg-white" data-testid="desktop-chat-panel">
        {selectedChat ? (
          <GuidedChatPage key={selectedChat} matchId={selectedChat} embedded />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center px-8">
            <div className="w-24 h-24 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: "rgba(249,171,13,0.12)" }}>
              <MessageCircle className="w-12 h-12" style={{ color: "#F9AB0D" }} />
            </div>
            <h2 className="text-2xl mb-2" style={{ ...serifFont, color: "#0A2A5A" }}>Your conversations live here</h2>
            <p className="text-sm text-slate-500 text-center max-w-sm">
              Select a conversation from the left to keep the connection going.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationsPage;
